import { useState, useEffect } from 'react';
import LightboxModal from './LightboxModal';

export default function GallerySection({ slug, title = 'Galleri', subtitle }) {
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(null);

  useEffect(() => {
    async function loadGallery() {
      try {
        setLoading(true);
        const res = await fetch('https://api.dev.auxo.dk/public/content/galleries');
        if (res.ok) {
          const items = await res.json();
          // Match by gallery_slug or slug
          const match = items.find(
            (g) =>
              g.gallery_slug === slug ||
              (g.slug && (g.slug === slug || g.slug._ === slug || g.slug._?.includes(slug)))
          );
          if (match) {
            setGallery(match);
          }
        }
      } catch (err) {
        console.error('Kunne ikke hente galleri fra CMS:', err);
      } finally {
        setLoading(false);
      }
    }

    if (slug) {
      loadGallery();
    }
  }, [slug]);

  const images = gallery?.images || [];

  if (loading) {
    return (
      <div className="py-12 flex justify-center">
        <div className="animate-pulse flex items-center gap-2 text-slate-400 font-medium">
          <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
          <span>Indlæser galleri...</span>
        </div>
      </div>
    );
  }

  if (images.length === 0) {
    return null;
  }

  return (
    <section className="py-16 bg-slate-50 border-t border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
            {subtitle || 'Glimt af vores grundige arbejde'}
          </h2>
          {gallery.description && (
            <p className="mt-3 text-slate-600 text-base max-w-2xl mx-auto">
              {gallery.description}
            </p>
          )}
        </div>

        {/* Image Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {images.map((imgUrl, index) => {
            // Apply Cloudflare image resize transformation for optimal performance
            const optimizedThumbnail = imgUrl.startsWith('https://assets.auxodev.dk/')
              ? imgUrl.replace(
                  'https://assets.auxodev.dk/',
                  'https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/'
                )
              : imgUrl;

            return (
              <button
                key={index}
                onClick={() => setActiveImageIndex(index)}
                className="group relative h-64 sm:h-72 w-full overflow-hidden transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-black text-left cursor-pointer"
                aria-label={`Åbn billede ${index + 1} i fuld størrelse`}
              >
                <img
                  src={optimizedThumbnail}
                  alt={`${gallery.title || 'Rengøring'} foto ${index + 1}`}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </button>
            );
          })}
        </div>
      </div>

      {/* Lightbox Modal */}
      {activeImageIndex !== null && (
        <LightboxModal
          images={images}
          activeIndex={activeImageIndex}
          onClose={() => setActiveImageIndex(null)}
          onNext={() => setActiveImageIndex((prev) => (prev + 1) % images.length)}
          onPrev={() => setActiveImageIndex((prev) => (prev - 1 + images.length) % images.length)}
        />
      )}
    </section>
  );
}
